import { targetedBreadCrumbs } from '../../../consts/targetData/breadCrumbs'
import { targetedSearchTerms } from '../../../consts/targetData/searchTerms'
import { BrandTargetSet, BreadCrumb, CategoryQueue, SearchTermCriteria } from '../types'
import { getPreviousChannelConfig } from '../utils/getPreviousChannelConfig'
import { logDevelopmentMessage } from '../utils/logDevelopmentMessage'

type WidgetVisibility = {
  shouldLoadWidget: boolean
  categoryApplicableForQueue: CategoryQueue
  isInitializingFromPreviousSession?: boolean
}
export type BrandBreadCrumbUrls = Record<
  string,
  { brandPartialUrlSet: BrandTargetSet[]; forceVisibleForCategory?: CategoryQueue }
>

export const checkWidgetVisibility = async (): Promise<WidgetVisibility> => {
  logDevelopmentMessage('checkWidgetVisibility: intending to render chat widget')

  const chatPreviouslyInitialized = getPreviousChannelConfig()
  const isInitializingFromPreviousSession = chatPreviouslyInitialized !== undefined

  if (isListingPage()) {
    return { shouldLoadWidget: false, categoryApplicableForQueue: null, isInitializingFromPreviousSession }
  }

  const breadCrumbCriteriaMet = assertIsTargetedBreadCrumbIncluded(targetedBreadCrumbs, targetedSearchTerms)
  if (breadCrumbCriteriaMet.shouldLoadWidget) {
    return {
      shouldLoadWidget: true,
      categoryApplicableForQueue: breadCrumbCriteriaMet.categoryApplicableForQueue,
      isInitializingFromPreviousSession,
    }
  }
  if (isInitializingFromPreviousSession) {
    // in this condition we are on a page that the widget shouldn't be visible on and so the widget itself should not be visible after closure
    // however the simultaneous chat should still initialize
    return { shouldLoadWidget: false, categoryApplicableForQueue: null, isInitializingFromPreviousSession }
  }

  return { shouldLoadWidget: false, categoryApplicableForQueue: null, isInitializingFromPreviousSession: false }
}

const isListingPage = () => {
  return ['search results', 'product list page grid'].includes(window?.jlData?.page?.pageInfo?.pageType)
}

const assertIsTargetedBreadCrumbIncluded = (
  breadCrumbRecord: Record<string, CategoryQueue>,
  targetedSearchTerms: SearchTermCriteria[]
) => {
  if (!window?.jlData?.page?.pageInfo?.breadCrumb) return { shouldLoadWidget: false, categoryApplicableForQueue: null }
  return (window?.jlData?.page?.pageInfo?.breadCrumb as BreadCrumb[])?.reduce<WidgetVisibility>(
    (acc, breadCrumb: BreadCrumb) => {
      if (acc.shouldLoadWidget) return acc
      if (typeof breadCrumb === 'string') return acc
      if (!breadCrumb?.value) return acc

      const value = breadCrumbRecord[breadCrumb?.value?.toLowerCase() ?? '']
      if (value) {
        return { shouldLoadWidget: true, categoryApplicableForQueue: value }
      }
      if (!breadCrumb?.value.includes('Search results')) return acc

      return assertSearchTermIsIncluded({ acc, searchTermString: breadCrumb.value, targetedSearchTerms })
    },
    { shouldLoadWidget: false, categoryApplicableForQueue: null }
  )
}

export const assertIsTargetedBreadCrumbIncludedInBrandPage = (brandBreadCrumbRecord: BrandBreadCrumbUrls) => {
  if (!window?.jlData?.page?.pageInfo?.breadCrumb) return { shouldLoadWidget: false, categoryApplicableForQueue: null }
  return (window?.jlData?.page?.pageInfo?.breadCrumb as BreadCrumb[])?.reduce<WidgetVisibility>(
    (acc, breadCrumb: BreadCrumb) => {
      if (acc.shouldLoadWidget) return acc
      if (typeof breadCrumb === 'string') return acc
      if (!breadCrumb?.value) return acc

      const value = brandBreadCrumbRecord[breadCrumb?.value ?? '']
      if (value) {
        if (value.forceVisibleForCategory) {
          return { shouldLoadWidget: true, categoryApplicableForQueue: value.forceVisibleForCategory }
        }
        return assertIsBrandTargetSetIncluded(value.brandPartialUrlSet)
      }
      return acc
    },
    { shouldLoadWidget: false, categoryApplicableForQueue: null }
  )
}

const assertIsBrandTargetSetIncluded = (brandTargetSet: BrandTargetSet[]) => {
  const categoryApplicableForSet = brandTargetSet.reduce<CategoryQueue>((acc, targetSet) => {
    if (acc) return acc

    const patternToTest = new RegExp(targetSet.partialUrls.join('|'), 'gi')
    const isPartialURLIncluded = patternToTest?.test(window.location.href)

    if (isPartialURLIncluded) return targetSet.categoryApplicableForQueue

    return null
  }, null)

  if (categoryApplicableForSet) return { shouldLoadWidget: true, categoryApplicableForQueue: categoryApplicableForSet }

  return { shouldLoadWidget: false, categoryApplicableForQueue: null }
}

const assertSearchTermIsIncluded = ({
  acc,
  searchTermString,
  targetedSearchTerms,
}: {
  acc: WidgetVisibility
  searchTermString: string
  targetedSearchTerms: SearchTermCriteria[]
}): WidgetVisibility => {
  // extract the search term in quotes
  const searchTerm = searchTermString.match(/‘([^']+)’/)?.[1]
  if (searchTermString === '') return acc

  for (let index = 0; index < targetedSearchTerms.length; index++) {
    const searchTermSet = targetedSearchTerms[index]
    if (searchTermSet.terms.length === 0) {
      continue
    }

    // this allows for full string matchign ie ^some search term$ anchors and matches the whole string. The OR (|) operator combines the full list
    const regexMainPatternString = searchTermSet.terms.join('$|^')

    // adding the anchors to the beginning and and of the string
    const regexPatternString = '^'.concat(regexMainPatternString).concat('$')
    const pattern = new RegExp(regexPatternString, 'gi')
    const isSearchTermIncluded = pattern?.test(searchTerm ?? '')

    if (isSearchTermIncluded) {
      // return breaks out of for loop if criteria is met
      return { shouldLoadWidget: true, categoryApplicableForQueue: searchTermSet.categoryApplicableForQueue }
    }
  }

  return acc
}
